import { useCallback, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useProjectSiteNavigation, useProjectSiteParams } from '@/shared/lib';
import { RouteNames } from '@/shared/config/routes';
import {
  useGetServiceRequestDetailQuery,
  useLinkServiceRequestToConsignmentMutation
} from '@/entities/service-request';
import type { SrfImportPreviewLocationState } from '@/features/srf-upload';
import { toSrfUserEntry, useSrfPrefill } from '@/widgets/consignment-edit-form';

import { toSrfFormFieldErrors, toSrfFormFieldHints, toSrfPreviewHints } from './srf-import-errors';

export function useConsignmentCreate() {
  const { t } = useTranslation();
  const { navigateWithContext } = useProjectSiteNavigation();
  const { projectId, siteId } = useProjectSiteParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [linkConsignment] = useLinkServiceRequestToConsignmentMutation();

  const serviceRequestParam = searchParams.get('serviceRequestId');
  const serviceRequestId = serviceRequestParam ? Number(serviceRequestParam) : undefined;
  const hasSrf = serviceRequestId != null && !Number.isNaN(serviceRequestId) && projectId != null && siteId != null;

  const { data: srfDetail, isFetching: isSrfLoading } = useGetServiceRequestDetailQuery(
    { id: serviceRequestId ?? 0, projectId: projectId ?? 0, siteId: siteId ?? 0 },
    { skip: !hasSrf }
  );

  const { prefillValues, prefillLabels, prefillHints, isResolving } = useSrfPrefill(hasSrf ? srfDetail : undefined);
  const srfUserEntry = useMemo(() => (srfDetail ? toSrfUserEntry(srfDetail) : undefined), [srfDetail]);

  // An SRF file import that failed validation lands here carrying its parsed data.
  // The service-request flow arrives through a query param instead, so the two
  // never prefill the same form — but a stale history entry could still hold both.
  const srfImportPreview = hasSrf
    ? undefined
    : (location.state as SrfImportPreviewLocationState | null)?.srfImportPreview;

  const [isImportAlertDismissed, setIsImportAlertDismissed] = useState(false);
  const dismissImportAlert = useCallback(() => setIsImportAlertDismissed(true), []);

  const importFieldErrors = useMemo(
    () => toSrfFormFieldErrors(srfImportPreview?.validationErrors ?? [], t),
    [srfImportPreview, t]
  );

  const fieldHints = useMemo(
    () => ({
      ...prefillHints,
      ...toSrfPreviewHints(srfImportPreview?.consignment),
      ...toSrfFormFieldHints(srfImportPreview?.validationErrors ?? [])
    }),
    [prefillHints, srfImportPreview]
  );

  const handleSave = useCallback(
    async (result: unknown) => {
      const id = (result as { id?: string } | null | undefined)?.id;

      if (id != null && hasSrf && serviceRequestId != null && projectId != null && siteId != null) {
        try {
          await linkConsignment({ id: serviceRequestId, consignmentId: id, projectId, siteId }).unwrap();
        } catch (error) {
          console.error('Failed to link service request to consignment:', error);
          toast.error(
            t('serviceRequests.linkConsignmentError', 'Consignment created, but copying service request lines failed')
          );
        }
      }

      if (id != null) {
        navigateWithContext(RouteNames.CONSIGNMENT_EDIT, { consignmentId: id });
      } else {
        navigateWithContext(RouteNames.CONSIGNMENTS);
      }
    },
    [navigateWithContext, linkConsignment, hasSrf, serviceRequestId, projectId, siteId, t]
  );

  const handleBack = useCallback(() => {
    navigateWithContext(RouteNames.CONSIGNMENTS);
  }, [navigateWithContext]);

  return {
    handleSave,
    handleBack,
    serviceRequestId: hasSrf ? serviceRequestId : undefined,
    srfDetail,
    prefillValues,
    prefillLabels,
    srfUserEntry,
    isSrfLoading: hasSrf ? isSrfLoading || isResolving : false,
    /** Parsed consignment of a `200` SRF import — feeds the form the same way an existing consignment does. */
    importedConsignment: srfImportPreview?.consignment,
    importValidationErrors: isImportAlertDismissed ? undefined : srfImportPreview?.validationErrors,
    importFieldErrors,
    fieldHints,
    dismissImportAlert
  };
}
